import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { AuthService } from '../../services/auth.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { SegmentService } from '../../services/segment.service';
import { GatewayPayoutService } from '../../services/paymentLink.service';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { Segment } from '../../interfaces/accounting.interface';
import { PaymentProvider, Payout, PayoutCandidates } from '../../interfaces/sales/paymentLink.interface';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';

/** W3-T: record a gateway payout (gross receipts, fee, net to the bank) and reverse one. */
const GatewayPayouts: React.FC = () => {
  const institutionId = AuthService.getSelectedInstitution() as string;
  const toast = useToast();
  const canManageReceipts = usePermission(PERMISSIONS.RECEIPT_MANAGE);
  const canManageBankRec = usePermission(PERMISSIONS.BANK_RECONCILIATION_MANAGE);
  const canManage = canManageReceipts && canManageBankRec;
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [reversing, setReversing] = useState<Payout | null>(null);

  const [provider, setProvider] = useState<PaymentProvider>('BKASH');
  const [candidates, setCandidates] = useState<PayoutCandidates | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedRefunds, setSelectedRefunds] = useState<Set<string>>(new Set()); // P1-B refund lines, ticked by default
  const [viewing, setViewing] = useState<Payout | null>(null); // P1-B payout detail
  const [banks, setBanks] = useState<BankAccount[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [bankId, setBankId] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [date, setDate] = useState(toLocalIsoDate());
  const [fee, setFee] = useState('0');
  const [ref, setRef] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setPayouts(await GatewayPayoutService.list(institutionId));
    } catch (e) {
      toast.showApiError(e, 'Failed to load gateway payouts');
    } finally {
      setLoading(false);
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const loadCandidates = useCallback(async (p: PaymentProvider) => {
    try {
      const c = await GatewayPayoutService.candidates(institutionId, p);
      setCandidates(c);
      setSelected(new Set());
      setSelectedRefunds(new Set((c.refunds ?? []).map(r => r.refundId))); // P1-B
    } catch (e) {
      toast.showApiError(e, 'Failed to load the receipts to pay out');
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = async () => {
    setCreating(true);
    try {
      const [b, s] = await Promise.all([BankAccountService.listByInstitution(institutionId), SegmentService.getByInstitution(0, 200)]);
      setBanks(b); setSegments(s?.content ?? []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load bank accounts');
    }
    await loadCandidates(provider);
  };

  const gross = useMemo(() => (candidates?.receipts ?? []).filter(r => selected.has(r.receiptId))
    .reduce((s, r) => s + r.amount, 0), [candidates, selected]);
  const refundTotal = useMemo(() => (candidates?.refunds ?? []).filter(r => selectedRefunds.has(r.refundId))
    .reduce((s, r) => s + r.amount, 0), [candidates, selectedRefunds]); // P1-B
  const feeNum = Number(fee) || 0;
  const net = gross - refundTotal - feeNum; // P1-B: receipts - refunds - fee

  useEffect(() => {
    if (candidates?.feeRatePct != null && gross > 0) {
      setFee((Math.round(gross * candidates.feeRatePct) / 100).toFixed(2));
    }
  }, [gross]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      setSaving(true);
      await GatewayPayoutService.create({
        institutionId, provider, payoutDate: date, bankAccountId: bankId, receiptIds: Array.from(selected), refundIds: Array.from(selectedRefunds),
        feeAmount: feeNum, feeSegmentId: feeNum > 0 ? segmentId || null : null, settlementReference: ref || null,
      });
      toast.showSuccess('Payout recorded');
      setCreating(false);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to record the payout');
    } finally {
      setSaving(false);
    }
  };
  const reverse = async () => {
    if (!reversing) return;
    try {
      await GatewayPayoutService.reverse(reversing.id);
      toast.showSuccess('Payout reversed');
      setReversing(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse the payout');
    }
  };

  const valid = selected.size > 0 && !!bankId && feeNum >= 0 && net > 0 && (feeNum === 0 || !!segmentId);

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.payouts.page">
      <Typography variant="h5" gutterBottom>Gateway Payouts</Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        When the gateway pays its collected money into your bank (less its fee), record it here so the clearing account empties.
      </Typography>
      {canManage && <Button variant="contained" sx={{ mb: 2 }} data-help="sales.payouts.record" onClick={openCreate}>Record payout</Button>}
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="sales.payouts.table">
          <Table size="small">
            <TableHead><TableRow>
              <TableCell data-help="sales.payouts.col.number">Number</TableCell><TableCell data-help="sales.payouts.col.provider">Provider</TableCell><TableCell data-help="sales.payouts.col.date">Date</TableCell>
              <TableCell align="right" data-help="sales.payouts.col.gross">Gross</TableCell><TableCell align="right" data-help="sales.payouts.col.fee">Fee</TableCell><TableCell align="right" data-help="sales.payouts.col.net">Net</TableCell>
              <TableCell data-help="sales.payouts.col.bank">Bank</TableCell><TableCell data-help="sales.payouts.col.status">Status</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {payouts.length === 0 && <TableRow><TableCell colSpan={9} align="center">No payouts yet.</TableCell></TableRow>}
              {payouts.map(p => (
                <TableRow key={p.id}>
                  <TableCell>{p.payoutNumber}</TableCell><TableCell>{humanizeEnum(p.provider)}</TableCell>
                  <TableCell>{formatDate(p.payoutDate)}</TableCell>
                  <TableCell align="right">{formatMoney(p.grossAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(p.feeAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(p.netAmount)}</TableCell>
                  <TableCell>{p.bankAccountName}</TableCell>
                  <TableCell><Chip size="small" label={humanizeEnum(p.status)} color={p.status === 'POSTED' ? 'success' : 'default'} /></TableCell>
                  <TableCell align="right">
                    <Button size="small" data-help="sales.payouts.action.view" onClick={() => setViewing(p)}>View</Button>
                    {canManage && p.status === 'POSTED' && <Button size="small" color="error" data-help="sales.payouts.action.reverse" onClick={() => setReversing(p)}>Reverse</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {creating && (
        <Dialog open onClose={() => setCreating(false)} maxWidth="md" fullWidth data-help-page="sales.payouts.create.page">
          <DialogTitle>Record gateway payout</DialogTitle>
          <DialogContent data-help-section="sales.payouts.create.section.form">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, pt: 1 }}>
              <TextField select size="small" data-help="sales.payouts.create.provider" label="Provider" value={provider} sx={{ flex: '1 1 160px', maxWidth: 220 }}
                onChange={e => { const p = e.target.value as PaymentProvider; setProvider(p); loadCandidates(p); }}>
                <MenuItem value="BKASH">bKash</MenuItem><MenuItem value="SSLCOMMERZ">SSLCommerz</MenuItem>
              </TextField>
              <TextField size="small" type="date" data-help="sales.payouts.create.date" label="Payout date" value={date} InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 160px', maxWidth: 220 }} onChange={e => setDate(e.target.value)} />
              <TextField select size="small" data-help="sales.payouts.create.bank" label="Bank account" value={bankId} sx={{ flex: '1 1 220px', maxWidth: 320 }}
                onChange={e => setBankId(e.target.value)}>
                {banks.map(b => <MenuItem key={b.id} value={b.id}>{b.bankName} {b.accountNumber}</MenuItem>)}
              </TextField>
              <TextField size="small" data-help="sales.payouts.create.reference" label="Settlement reference" value={ref} sx={{ flex: '1 1 180px', maxWidth: 240 }}
                onChange={e => setRef(e.target.value)} />
            </Box>
            <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 280 }} data-help-section="sales.payouts.create.receipts">
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell /><TableCell data-help="sales.payouts.create.receipts.col.receipt">Receipt</TableCell><TableCell data-help="sales.payouts.create.receipts.col.date">Date</TableCell><TableCell data-help="sales.payouts.create.receipts.col.reference">Reference</TableCell><TableCell align="right" data-help="sales.payouts.create.receipts.col.amount">Amount</TableCell></TableRow></TableHead>
                <TableBody>
                  {(candidates?.receipts ?? []).length === 0 && <TableRow><TableCell colSpan={5} align="center">No online receipts waiting to be paid out.</TableCell></TableRow>}
                  {(candidates?.receipts ?? []).map(r => (
                    <TableRow key={r.receiptId}>
                      <TableCell padding="checkbox" data-help="sales.payouts.create.receipts.select"><Checkbox checked={selected.has(r.receiptId)} onChange={e => {
                        const n = new Set(selected); if (e.target.checked) n.add(r.receiptId); else n.delete(r.receiptId); setSelected(n);
                      }} /></TableCell>
                      <TableCell>{r.receiptNumber}</TableCell><TableCell>{r.receiptDate ? formatDate(r.receiptDate) : ''}</TableCell>
                      <TableCell>{r.reference}</TableCell><TableCell align="right">{formatMoney(r.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* ---- P1-B refund lines netted in this payout ---- */}
            {(candidates?.refunds ?? []).length > 0 && (
              <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 200 }} data-help-section="sales.payouts.create.refunds">
                <Table size="small" stickyHeader>
                  <TableHead><TableRow><TableCell /><TableCell data-help="sales.payouts.create.refunds.col.creditNote">Refund of credit note</TableCell><TableCell data-help="sales.payouts.create.refunds.col.date">Date</TableCell><TableCell data-help="sales.payouts.create.refunds.col.ref">Gateway ref</TableCell><TableCell align="right" data-help="sales.payouts.create.refunds.col.amount">Amount</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(candidates?.refunds ?? []).map(r => (
                      <TableRow key={r.refundId}>
                        <TableCell padding="checkbox" data-help="sales.payouts.create.refunds.select"><Checkbox checked={selectedRefunds.has(r.refundId)} inputProps={{ 'aria-label': `Net refund ${r.creditNoteNumber ?? r.refundId}` }} onChange={e => {
                          const n = new Set(selectedRefunds); if (e.target.checked) n.add(r.refundId); else n.delete(r.refundId); setSelectedRefunds(n);
                        }} /></TableCell>
                        <TableCell>{r.creditNoteNumber}</TableCell><TableCell>{r.date ? formatDate(r.date) : ''}</TableCell>
                        <TableCell>{r.providerRefundId}</TableCell><TableCell align="right">{formatMoney(-r.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {/* ---- end P1-B ---- */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField size="small" type="number" data-help="sales.payouts.create.fee" label="Gateway fee" value={fee} sx={{ flex: '1 1 160px', maxWidth: 220 }}
                onChange={e => setFee(e.target.value)} />
              {feeNum > 0 && (
                <TextField select size="small" data-help="sales.payouts.create.feeSegment" label="Fee charged to segment" value={segmentId} sx={{ flex: '1 1 220px', maxWidth: 320 }}
                  onChange={e => setSegmentId(e.target.value)}>
                  {segments.map(s => <MenuItem key={s.id} value={s.id}>{s.code} - {s.name}</MenuItem>)}
                </TextField>
              )}
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              Receipts {formatMoney(gross)} - refunds {formatMoney(refundTotal)} - fee {formatMoney(feeNum)} = net to bank {formatMoney(net)}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.payouts.create.cancel" onClick={() => setCreating(false)}>Cancel</Button>
            <Button variant="contained" disabled={!valid || saving} data-help="sales.payouts.create.save" onClick={save}>Record payout</Button>
          </DialogActions>
        </Dialog>
      )}
      {/* ---- P1-B payout detail ---- */}
      {viewing && (
        <Dialog open onClose={() => setViewing(null)} maxWidth="md" fullWidth data-help-page="sales.payouts.view.page">
          <DialogTitle>Payout {viewing.payoutNumber}</DialogTitle>
          <DialogContent data-help-section="sales.payouts.view.section.main">
            <TableContainer component={Paper} sx={{ mb: 2 }} data-help-section="sales.payouts.view.receipts">
              <Table size="small">
                <TableHead><TableRow><TableCell data-help="sales.payouts.view.receipts.col.receipt">Receipt</TableCell><TableCell data-help="sales.payouts.view.receipts.col.date">Date</TableCell><TableCell data-help="sales.payouts.view.receipts.col.reference">Reference</TableCell><TableCell align="right" data-help="sales.payouts.view.receipts.col.amount">Amount</TableCell></TableRow></TableHead>
                <TableBody>
                  {viewing.lines.map(l => (
                    <TableRow key={l.receiptId}><TableCell>{l.receiptNumber}</TableCell><TableCell>{l.receiptDate ? formatDate(l.receiptDate) : ''}</TableCell>
                      <TableCell>{l.reference}</TableCell><TableCell align="right">{formatMoney(l.amount)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {(viewing.refundLines ?? []).length > 0 && (
              <TableContainer component={Paper} sx={{ mb: 2 }} data-help-section="sales.payouts.view.refunds">
                <Table size="small">
                  <TableHead><TableRow><TableCell data-help="sales.payouts.view.refunds.col.creditNote">Refund of credit note</TableCell><TableCell data-help="sales.payouts.view.refunds.col.date">Date</TableCell><TableCell data-help="sales.payouts.view.refunds.col.ref">Gateway ref</TableCell><TableCell align="right" data-help="sales.payouts.view.refunds.col.amount">Amount</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(viewing.refundLines ?? []).map(r => (
                      <TableRow key={r.refundId}><TableCell>{r.creditNoteNumber}</TableCell><TableCell>{r.date ? formatDate(r.date) : ''}</TableCell>
                        <TableCell>{r.providerRefundId}</TableCell><TableCell align="right">{formatMoney(-r.amount)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Alert severity="info">
              Receipts {formatMoney(viewing.grossAmount)} - refunds {formatMoney(viewing.refundAmount ?? 0)} - fee {formatMoney(viewing.feeAmount)} = net to bank {formatMoney(viewing.netAmount)}
            </Alert>
          </DialogContent>
          <DialogActions><Button data-help="sales.payouts.view.close" onClick={() => setViewing(null)}>Close</Button></DialogActions>
        </Dialog>
      )}
      {/* ---- end P1-B ---- */}
      {reversing && (
        <Dialog open onClose={() => setReversing(null)} maxWidth="xs" fullWidth data-help-page="sales.payouts.reverse.page">
          <DialogTitle>Reverse payout {reversing.payoutNumber}?</DialogTitle>
          <DialogContent data-help-section="sales.payouts.reverse.section.main">
            <Typography variant="body2">
              The bank and fee postings are reversed and the receipts return to the clearing account, ready for a corrected payout.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.payouts.reverse.keep" onClick={() => setReversing(null)}>Keep</Button>
            <Button color="error" variant="contained" data-help="sales.payouts.reverse.confirm" onClick={reverse}>Reverse</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default GatewayPayouts;
